import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Play } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const CostPerVideoViewCalculator = () => {
  const [adSpend, setAdSpend] = useState(1000);
  const [videoViews, setVideoViews] = useState(50000);
  const [costPerView, setCostPerView] = useState(0);

  useEffect(() => {
    calculateCostPerView();
  }, [adSpend, videoViews]);

  const calculateCostPerView = () => {
    const cpv = adSpend / videoViews;
    setCostPerView(parseFloat(cpv.toFixed(4)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/cost-per-video-view-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Cost Per Video View Calculator: Optimize Video Ad Spend | Free Tool</title>
        <meta name="description" content="Calculate your Cost Per Video View instantly. Compare with industry benchmarks, get optimization tips, and improve video advertising efficiency." />
        <meta name="keywords" content="cost per video view calculator, video ad metrics, CPV optimization, video advertising, ad performance" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Cost Per Video View Calculator: Optimize Ad Spend" />
        <meta property="og:description" content="Calculate your Cost Per Video View instantly. Get actionable insights to improve video ad performance and reduce costs." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Cost Per Video View Calculator: Optimize Ad Spend" />
        <meta property="twitter:description" content="Calculate your Cost Per Video View instantly. Get actionable insights to improve video ad performance and reduce costs." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Cost Per Video View Calculator",
            "description": "Free calculator to measure and optimize video advertising costs",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate CPV",
              "Industry Benchmarks",
              "Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Cost Per Video View",
              "description": "The average cost to generate one video view through paid advertising."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Video Advertisers, Media Buyers, Marketing Managers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Cost Per Video View",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total ad spend"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of video views"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your CPV and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Cost Per Video View Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Ad Spend</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={adSpend}
                  onChange={(e) => setAdSpend(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Video Views</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Play className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={videoViews}
                  onChange={(e) => setVideoViews(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="50000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            ${costPerView}
            <span className="text-xl text-gray-500 ml-2">per view</span>
          </div>
          <p className="text-gray-600">Cost Per Video View</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Cost Per Video View is ${costPerView}. This means you're spending an average of ${costPerView} for each view of your video ad.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Platform Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>YouTube: $0.01-0.03</li>
          <li>Facebook: $0.01-0.04</li>
          <li>Instagram: $0.02-0.05</li>
          <li>TikTok: $0.01-0.03</li>
          <li>LinkedIn: $0.05-0.08</li>
          <li>Twitter: $0.02-0.04</li>
          <li>Snapchat: $0.01-0.03</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Cost Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Create engaging content</li>
          <li>Optimize targeting</li>
          <li>Use compelling thumbnails</li>
          <li>Test video lengths</li>
          <li>Implement retargeting</li>
          <li>Test ad placements</li>
          <li>Monitor performance</li>
          <li>Focus on quality views</li>
          <li>Optimize bidding strategy</li>
          <li>A/B test creatives</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">View Quality Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>View Duration</li>
          <li>Engagement Rate</li>
          <li>Completion Rate</li>
          <li>Click-Through Rate</li>
          <li>Audience Retention</li>
          <li>Social Interactions</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Better targeting: -30% CPV</li>
          <li>Engaging content: -25% CPV</li>
          <li>Optimal timing: -20% CPV</li>
          <li>Platform selection: -15% CPV</li>
          <li>Audience segmentation: -35% CPV</li>
        </ul>
      </div>
    </div>
  );
};

export default CostPerVideoViewCalculator;
